// Thank you https://inclusive-components.design/cards/
// -- see "The pseudo-content trick"

import React, { ReactNode, useEffect } from "react";
import { cn } from "../utils/cn";

type ProductCardProps = {
  title: string;
  vendor?: string;
  description: ReactNode;
  imageSrc: string;
  imageAlt?: string;
  secondaryImageSrc?: string;
  secondaryImageAlt?: string;
  link?: string;
  onProductClick?: () => void;
  badge?: ReactNode;
  aspectRatio?: string;
  isLoading?: boolean;
  popOverContent?: ReactNode;
  bgColor?: string;
  compareAtPrice: string;
};

const TOUCH_TIMEOUT = 300; // Time in ms to differentiate between tap and long press
const isMobile =
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

const ProductCard: React.FC<ProductCardProps> = ({
  title,
  vendor,
  description,
  imageSrc,
  imageAlt = "",
  secondaryImageSrc,
  secondaryImageAlt = "",
  link,
  onProductClick = () => {},
  badge = null,
  popOverContent = null,
  aspectRatio = "4/3",
  isLoading = false,
  bgColor,
  compareAtPrice,
}) => {
  const onTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!secondaryImageSrc) return; // Only apply touch effects if there's a secondary image
    window.pressId = setTimeout(
      (element) => {
        element.dataset.pressed = "true";
      },
      TOUCH_TIMEOUT,
      e.currentTarget,
    );
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (!secondaryImageSrc) return; // Only apply touch effects if there's a secondary image
    clearTimeout(window?.pressId);
    e.currentTarget.dataset.pressed = null;
  };

  // Map aspect ratios to actual Tailwind classes
  const aspectRatioClasses = {
    "1/1": "tw-aspect-square",
    "4/3": "tw-aspect-[4/3]",
    "3/2": "tw-aspect-[3/2]",
    "16/9": "tw-aspect-video",
    "2/1": "tw-aspect-[2/1]",
  } as const;

  const aspectClass =
    aspectRatioClasses[aspectRatio as keyof typeof aspectRatioClasses] ||
    "tw-aspect-[4/3]";
  const bgColorClass = bgColor
    ? `before:${bgColor}`
    : "before:tw-bg-black/[0.04]";

  return (
    <div
      style={{
        WebkitUserSelect: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
      }}
      className={cn(
        "tw-relative tw-group tw-flex tw-flex-col tw-overflow-hidden",
        link && !secondaryImageSrc && "[&_img]:hover:tw-scale-[1.03]",
        isLoading && "tw-opacity-50",
      )}
    >
      <div
        onContextMenu={(e) => {
          if (!secondaryImageSrc) return; // Only prevent context menu if there's a secondary image
          e.preventDefault();
          e.stopPropagation();
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-mobile={isMobile}
        className={cn(
          aspectClass,
          bgColorClass,
          "tw-w-full tw-overflow-hidden",
          "tw-relative before:tw-absolute before:tw-content-[''] group before:tw-block before:tw-z-[1] before:tw-inset-0",
          "tw-group",
        )}
      >
        {link && (
          <a
            href={link}
            onClick={onProductClick}
            className="tw-absolute tw-inset-0 tw-z-[2]"
            aria-hidden="true"
          ></a>
        )}
        <img
          src={imageSrc}
          alt={imageAlt}
          className={cn(
            "tw-h-full tw-w-full tw-object-contain",
            !secondaryImageSrc
              ? "tw-transition-transform tw-duration-200 tw-ease-out"
              : "!tw-transition-none",
          )}
        />
        {secondaryImageSrc && (
          <img
            src={secondaryImageSrc}
            alt={secondaryImageAlt}
            className={cn(
              "tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover",
              "tw-transition-opacity tw-duration-200 tw-ease-out tw-z-[1] tw-pointer-events-none tw-opacity-0 group-hover:group-data-[mobile='false']:tw-opacity-100 group-data-[pressed='true']:tw-opacity-100",
            )}
          />
        )}

        {/* BADGE */}
        {badge && (
          <div
            className={cn(
              "tw-absolute tw-z-[1] tw-top-[10px] tw-left-[20px] tw-text-[13px]",
              secondaryImageSrc && "tw-bg-[#f5f5f5] tw-px-2 tw-py-1 tw-rounded",
            )}
          >
            {badge}
          </div>
        )}

        {/* POPUP */}
        {popOverContent && (
          <div
            className="tw-bg-white tw-w-full tw-max-h-[80px] tw-py-3 tw-absolute tw-z-[1] tw-bottom-0
                        tw-transform tw-translate-y-full group-hover:tw-translate-y-0
                        tw-transition-transform tw-duration-200
                        tw-grid tw-grid-cols-2"
          >
            {popOverContent}
          </div>
        )}
      </div>

      <div className="tw-flex tw-flex-row tw-justify-between tw-gap-x-5 tw-mt-1">
        <p>
          {link ? (
            <a href={link} onClick={onProductClick}>
              {vendor ? vendor + " " + title : title}
            </a>
          ) : (
            <span>{vendor ? vendor + " " + title : title}</span>
          )}
        </p>

        <div className="tw-flex tw-flex-col">
          {compareAtPrice !== "" && (
            <div className="tw-text-gray-600 tw-line-through">
              {compareAtPrice}
            </div>
          )}
          {description !== "$0.00" && (
            <div className="tw-text-gray-600">{description}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
